import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PaginationControls from './PaginationControls';
import { FaStar, FaRegStar, FaTrash, FaShare } from 'react-icons/fa'; // Added FaShare icon

interface Movie {
  id: string;
  filename: string;
  upload_date: string;
  recorded_date: string | null;
  s3_link: string;
  still_link: string;
  tags: string[];
  favorite: boolean;
}

interface PaginatedResponse {
  movies: Movie[];
  total: number;
  page: number;
  per_page: number;
}

function MovieList() {
  const navigate = useNavigate();
  const location = useLocation();
  const perPage = 12;

  // Initialize state from URL parameters
  const [currentPage, setCurrentPage] = useState(() => {
    const params = new URLSearchParams(location.search);
    return parseInt(params.get('page') || '1', 10);
  });
  const [favoritesOnly, setFavoritesOnly] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('favorites') === 'true';
  });
  const [hideNoAnimalTags, setHideNoAnimalTags] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('no_tags') !== 'false';
  });
  const [searchTerm, setSearchTerm] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  });

  const [movies, setMovies] = useState<Movie[]>([]);
  const [totalMovies, setTotalMovies] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const totalPages = Math.ceil(totalMovies / perPage);

  // Update URL when parameters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (currentPage !== 1) params.set('page', currentPage.toString());
    if (favoritesOnly) params.set('favorites', 'true');
    params.set('no_tags', hideNoAnimalTags.toString());
    if (searchTerm) params.set('search', searchTerm);
    navigate({ search: params.toString() }, { replace: true });
  }, [currentPage, favoritesOnly, hideNoAnimalTags, searchTerm, navigate]);

  // Modify the fetch movies effect to remove URL conversion
  useEffect(() => {
    setIsLoading(true);
    const fetchParams = new URLSearchParams({
      page: currentPage.toString(),
      per_page: perPage.toString(),
      favorites_only: favoritesOnly.toString(),
      exclude_no_tags: hideNoAnimalTags.toString(),
      search: searchTerm,
    });

    fetch(`/api/movies?${fetchParams.toString()}`)
      .then((response) => response.json())
      .then((data: PaginatedResponse) => {
        setMovies(data.movies);
        setTotalMovies(data.total);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching movies:', error);
        setIsLoading(false);
      });
  }, [currentPage, favoritesOnly, hideNoAnimalTags, searchTerm]);

  // Event handlers
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleToggleFavorites = () => {
    setFavoritesOnly((prev) => !prev);
    setCurrentPage(1); // Reset to first page
  };

  const handleToggleNoAnimalTags = () => {
    setHideNoAnimalTags((prev) => !prev);
    setCurrentPage(1); // Reset to first page
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page
  };

  const handleMovieClick = (movie: Movie) => {
    navigate(`/movie/${movie.id}`, { state: { from: location } });
  };

  // Handle navigation when clicking on the card
  const handleCardClick = (movie: Movie) => {
    handleMovieClick(movie);
  };

  // Function to toggle favorite status
  const toggleFavorite = (movieId: string, currentFavorite: boolean, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    fetch(`/api/movies/${movieId}/favorite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ favorite: !currentFavorite }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to update favorite status');
        }
        return response.json();
      })
      .then(() => {
        // Update the movie's favorite status in state
        setMovies((prevMovies) =>
          prevMovies.map((movie) =>
            movie.id === movieId ? { ...movie, favorite: !currentFavorite } : movie
          )
        );
      })
      .catch((error) => {
        console.error('Error updating favorite status:', error);
      });
  };

  // Function to delete a movie
  const handleDelete = (movieId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click

    if (!window.confirm('Are you sure you want to delete this movie?')) {
      return;
    }

    fetch(`/api/movies/${movieId}`, {
      method: 'DELETE',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to delete movie');
        }
        return response.json();
      })
      .then(() => {
        // Remove the movie from state
        setMovies((prevMovies) => prevMovies.filter((movie) => movie.id !== movieId));
        // Optionally, decrease totalMovies
        setTotalMovies((prevTotal) => prevTotal - 1);
      })
      .catch((error) => {
        console.error('Error deleting movie:', error);
      });
  };

  // New state for direct page lookup
  const [directPage, setDirectPage] = useState<string>('');

  // Function to handle direct page navigation
  const handleDirectPageChange = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const pageNumber = parseInt(directPage, 10);
    if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      setDirectPage('');
    } else {
      alert('Please enter a valid page number.');
    }
  };

  // Function to handle sharing
  const handleShare = (movie: Movie, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    const shareUrl = `${window.location.origin}/movie/${movie.id}?still=${encodeURIComponent(movie.still_link)}&video=${encodeURIComponent(movie.s3_link)}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      alert('Link copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy link: ', err);
    });
  };

  // Render component
  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold mb-4">Movie List</h1>

      {/* Search and Favorites Toggle */}
      <div className="flex justify-between items-center mb-4">
        <input
          type="text"
          placeholder="Search movies..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="w-1/2 p-2 border border-gray-300 rounded"
        />
        <div className="flex gap-2">
          <button
            onClick={handleToggleFavorites}
            className={`p-2 rounded ${
              favoritesOnly ? 'bg-yellow-500 text-white' : 'bg-gray-200'
            }`}
          >
            {favoritesOnly ? 'Show All' : 'Show Favorites'}
          </button>
          <button
            onClick={handleToggleNoAnimalTags}
            className={`p-2 rounded ${
              hideNoAnimalTags ? 'bg-yellow-500 text-white' : 'bg-gray-200'
            }`}
          >
            {hideNoAnimalTags ? 'Show All Videos' : 'Hide No Animals'}
          </button>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
        {/* Direct Page Lookup Form */}
        <form onSubmit={handleDirectPageChange} className="flex items-center">
          <label htmlFor="directPage" className="mr-2 text-sm text-gray-700">
            Go to page:
          </label>
          <input
            type="number"
            id="directPage"
            name="directPage"
            value={directPage}
            onChange={(e) => setDirectPage(e.target.value)}
            className="w-16 p-1 border border-gray-300 rounded mr-2"
            min={1}
            max={totalPages}
            placeholder={`${currentPage}`}
          />
          <button
            type="submit"
            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Go
          </button>
        </form>
      </div>

      {/* Movie Grid */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {movies.map((movie) => (
            <div
              key={movie.id}
              className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer"
              onClick={() => handleCardClick(movie)}
            >
              <img
                src={movie.still_link} // This is already the tref.fun URL from the backend
                alt={movie.filename}
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h2 className="text-lg font-semibold mb-1">
                  {formatTimestamp(movie.recorded_date || movie.upload_date)}
                </h2>
                <p className="text-sm text-gray-500 mb-1">Filename: {movie.filename}</p>
                <p className="text-sm text-gray-500 mb-2">
                  Uploaded: {formatTimestamp(movie.upload_date)}
                </p>
                <div className="flex flex-wrap gap-1 mb-2 min-h-[1.5rem]">
                  {movie.tags.map((tag, index) => (
                    <span
                      key={`${movie.id}-${tag}-${index}`}
                      className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                {/* Additional buttons can be added here */}

                <div className="flex items-center mt-2">
                  {/* Favorite Button */}
                  <button
                    onClick={(e) => toggleFavorite(movie.id, movie.favorite, e)}
                    className="focus:outline-none mr-2"
                    aria-label={movie.favorite ? 'Unfavorite' : 'Favorite'}
                  >
                    {movie.favorite ? (
                      <FaStar className="text-yellow-500 w-6 h-6" />
                    ) : (
                      <FaRegStar className="text-gray-400 w-6 h-6" />
                    )}
                  </button>

                  {/* Share Button */}
                  <button
                    onClick={(e) => handleShare(movie, e)}
                    className="focus:outline-none mr-2"
                    aria-label="Share"
                  >
                    <FaShare className="text-blue-500 w-6 h-6" />
                  </button>

                  {/* Delete Button */}
                  <button
                    onClick={(e) => handleDelete(movie.id, e)}
                    className="text-red-500 focus:outline-none"
                    aria-label="Delete"
                  >
                    <FaTrash className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-center mt-4">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
}

function formatTimestamp(date: string | null): string {
  if (!date) return 'Unknown';
  return new Date(date).toLocaleString();
}

export default MovieList;
